import React, { useEffect, useRef } from "react";

import { DashboardWindow } from "./dashboardHelpers";

interface Options {
  /** Skip registration entirely — e.g. a picker panel that is currently closed. */
  enabled?: boolean;
  /**
   * Listen in the CAPTURE phase and stop propagation when dismissing. Use this for a control
   * nested inside another dismissable surface, so the inner one closes in its own step:
   * - Escape: the outer surface's bubble-phase keydown would close the whole panel; stopping the
   *   captured event closes only the inner control.
   * - mousedown: bubble phase never fires for clicks on the outer surface's own controls (edit
   *   popovers stop mousedown propagation at their root), which would leave the inner control
   *   stuck open — and a click outside would tear down the whole panel, dropping unsaved edits.
   * stopPropagation does not cancel default behavior, so the intercepted click still focuses
   * inputs, and click-driven controls (toggles, buttons) still fire.
   */
  capture?: boolean;
}

/**
 * Dismiss a floating surface on an outside mousedown or on Escape.
 *
 * Listens on `targetWindow.document`, not the ambient `document`: the dashboard renders inside an
 * iframe, so listeners on the parent document never see these events.
 */
export function useDismissOnOutside(
  ref: React.RefObject<HTMLElement>,
  onDismiss: () => void,
  targetWindow: DashboardWindow,
  { enabled = true, capture = false }: Options = {}
): void {
  // Held in a ref so an inline arrow from the caller doesn't re-register the listeners each render.
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        if (capture) {
          e.stopPropagation();
        }
        onDismissRef.current();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (capture) {
          e.stopPropagation();
        }
        onDismissRef.current();
      }
    };

    const doc = targetWindow.document;
    doc.addEventListener("mousedown", handleMouseDown, { capture });
    doc.addEventListener("keydown", handleEscape, { capture });

    return () => {
      doc.removeEventListener("mousedown", handleMouseDown, { capture });
      doc.removeEventListener("keydown", handleEscape, { capture });
    };
  }, [ref, targetWindow, enabled, capture]);
}
